'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  Monitor,
  Workflow,
  Puzzle,
  Shield,
  Headphones,
  Users,
  Zap,
  FileText,
  LayoutGrid,
  GitBranch,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { NodeData } from '@/lib/specmap/types';

interface NodeProps {
  node: NodeData;
  isSelected: boolean;
  isHighlighted: boolean;
  onSelect: () => void;
  onHover: (id: string | null) => void;
  laneColor: string;
  nodeRef?: (element: HTMLDivElement | null) => void;
  onPositionChange?: (id: string, position: { x: number; y: number }) => void;
}

const STATUS_CONFIG: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  Draft: {
    label: 'Draft',
    className: 'badge-draft',
    icon: <FileText data-component-id="src/app/components/SpecMapNode.tsx:39:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="39" data-component-end-line="39" data-component-file="SpecMapNode.tsx" data-component-name="FileText" data-component-content="%7B%22elementName%22%3A%22FileText%22%7D" size={9} />,
  },
  'In Review': {
    label: 'In Review',
    className: 'badge-review',
    icon: <Clock data-component-id="src/app/components/SpecMapNode.tsx:44:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="44" data-component-end-line="44" data-component-file="SpecMapNode.tsx" data-component-name="Clock" data-component-content="%7B%22elementName%22%3A%22Clock%22%7D" size={9} />,
  },
  Approved: {
    label: 'Approved',
    className: 'badge-approved',
    icon: <CheckCircle2 data-component-id="src/app/components/SpecMapNode.tsx:49:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="49" data-component-end-line="49" data-component-file="SpecMapNode.tsx" data-component-name="CheckCircle2" data-component-content="%7B%22elementName%22%3A%22CheckCircle2%22%7D" size={9} />,
  },
  Complete: {
    label: 'Complete',
    className: 'badge-complete',
    icon: <CheckCircle2 data-component-id="src/app/components/SpecMapNode.tsx:54:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="54" data-component-end-line="54" data-component-file="SpecMapNode.tsx" data-component-name="CheckCircle2" data-component-content="%7B%22elementName%22%3A%22CheckCircle2%22%7D" size={9} />,
  },
  Deprecated: {
    label: 'Deprecated',
    className: 'badge-deprecated',
    icon: <AlertTriangle data-component-id="src/app/components/SpecMapNode.tsx:59:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="59" data-component-end-line="59" data-component-file="SpecMapNode.tsx" data-component-name="AlertTriangle" data-component-content="%7B%22elementName%22%3A%22AlertTriangle%22%7D" size={9} />,
  },
};

const PRIORITY_CONFIG: Record<string, { className: string }> = {
  High: { className: 'badge-high' },
  Medium: { className: 'badge-medium' },
  Low: { className: 'badge-low' },
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Module: <LayoutGrid data-component-id="src/app/components/SpecMapNode.tsx:70:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="70" data-component-end-line="70" data-component-file="SpecMapNode.tsx" data-component-name="LayoutGrid" data-component-content="%7B%22elementName%22%3A%22LayoutGrid%22%7D" size={12} />,
  Screen: <Monitor data-component-id="src/app/components/SpecMapNode.tsx:71:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="71" data-component-end-line="71" data-component-file="SpecMapNode.tsx" data-component-name="Monitor" data-component-content="%7B%22elementName%22%3A%22Monitor%22%7D" size={12} />,
  Workflow: <Workflow data-component-id="src/app/components/SpecMapNode.tsx:72:12" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="72" data-component-end-line="72" data-component-file="SpecMapNode.tsx" data-component-name="Workflow" data-component-content="%7B%22elementName%22%3A%22Workflow%22%7D" size={12} />,
  Integration: <Puzzle data-component-id="src/app/components/SpecMapNode.tsx:73:15" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="73" data-component-end-line="73" data-component-file="SpecMapNode.tsx" data-component-name="Puzzle" data-component-content="%7B%22elementName%22%3A%22Puzzle%22%7D" size={12} />,
  Role: <Users data-component-id="src/app/components/SpecMapNode.tsx:74:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="74" data-component-end-line="74" data-component-file="SpecMapNode.tsx" data-component-name="Users" data-component-content="%7B%22elementName%22%3A%22Users%22%7D" size={12} />,
  Admin: <Shield data-component-id="src/app/components/SpecMapNode.tsx:75:9" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="75" data-component-end-line="75" data-component-file="SpecMapNode.tsx" data-component-name="Shield" data-component-content="%7B%22elementName%22%3A%22Shield%22%7D" size={12} />,
  Support: <Headphones data-component-id="src/app/components/SpecMapNode.tsx:76:11" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="76" data-component-end-line="76" data-component-file="SpecMapNode.tsx" data-component-name="Headphones" data-component-content="%7B%22elementName%22%3A%22Headphones%22%7D" size={12} />,
  External: <Zap data-component-id="src/app/components/SpecMapNode.tsx:77:12" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="77" data-component-end-line="77" data-component-file="SpecMapNode.tsx" data-component-name="Zap" data-component-content="%7B%22elementName%22%3A%22Zap%22%7D" size={12} />,
};

export default function SpecMapNode({
  node,
  isSelected,
  isHighlighted,
  onSelect,
  onHover,
  laneColor,
  nodeRef,
  onPositionChange,
}: NodeProps) {
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [localPosition, setLocalPosition] = useState({
    x: node.positionX ?? 0,
    y: node.positionY ?? 0,
  });
  const movedRef = useRef(false);
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
  });
  const status = STATUS_CONFIG[node.status] || STATUS_CONFIG.Draft;
  const priority = PRIORITY_CONFIG[node.priority] || PRIORITY_CONFIG.Medium;

  useEffect(() => {
    if (!dragging) {
      setLocalPosition({
        x: node.positionX ?? 0,
        y: node.positionY ?? 0,
      });
    }
  }, [dragging, node.id, node.positionX, node.positionY]);

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      event.button !== 0 ||
      (event.target as HTMLElement).closest('button, input, textarea, select, a')
    ) {
      return;
    }

    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      baseX: localPosition.x,
      baseY: localPosition.y,
    };
    movedRef.current = false;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - dragRef.current.startX;
      const dy = moveEvent.clientY - dragRef.current.startY;

      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        movedRef.current = true;
        setDragging(true);
      }

      if (movedRef.current) {
        setLocalPosition({
          x: dragRef.current.baseX + dx,
          y: dragRef.current.baseY + dy,
        });
      }
    };

    const handlePointerUp = (upEvent: PointerEvent) => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      setDragging(false);

      const dx = upEvent.clientX - dragRef.current.startX;
      const dy = upEvent.clientY - dragRef.current.startY;

      if (movedRef.current) {
        const nextPosition = {
          x: dragRef.current.baseX + dx,
          y: dragRef.current.baseY + dy,
        };
        setLocalPosition(nextPosition);
        onPositionChange?.(node.id, nextPosition);
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleSelect = () => {
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }

    onSelect();
  };

  return (
    <div data-component-id="src/app/components/SpecMapNode.tsx:185:4" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="185" data-component-end-line="329" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      ref={nodeRef}
      data-node={node.id}
      onClick={handleSelect}
      onPointerDown={handlePointerDown}
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
      className={`
        relative flex flex-col bg-card rounded-xl border transition-all duration-200 cursor-pointer
        w-56 flex-shrink-0
        ${isSelected
          ? 'node-shadow-selected border-primary/40'
          : isHighlighted
            ? 'border-amber/40 node-shadow-hover'
            : 'border-border node-shadow hover:node-shadow-hover hover:border-border/80'
        }
        ${dragging ? 'z-30 opacity-95' : ''}
      `}
      style={{
        borderTopColor: isSelected ? laneColor : undefined,
        borderTopWidth: isSelected ? '2px' : undefined,
        transform: `translate(${localPosition.x}px, ${localPosition.y}px)`,
      }}
    >
      {/* Selected indicator pulse */}
      {isSelected && (
        <div data-component-id="src/app/components/SpecMapNode.tsx:211:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="211" data-component-end-line="214" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20-top-px%20-left-px%20w-2%20h-2%20rounded-full%22%7D"
          className="absolute -top-px -left-px w-2 h-2 rounded-full"
          style={{ background: laneColor }}
        />
      )}

      {/* Card header */}
      <div data-component-id="src/app/components/SpecMapNode.tsx:218:6" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="218" data-component-end-line="246" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-3.5%20pt-3.5%20pb-2.5%22%7D" className="px-3.5 pt-3.5 pb-2.5">
        {/* Category + priority row */}
        <div data-component-id="src/app/components/SpecMapNode.tsx:220:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="220" data-component-end-line="235" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-2%22%7D" className="flex items-center justify-between mb-2">
          <div data-component-id="src/app/components/SpecMapNode.tsx:221:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="221" data-component-end-line="231" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1.5%22%7D" className="flex items-center gap-1.5">
            <div data-component-id="src/app/components/SpecMapNode.tsx:222:12" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="222" data-component-end-line="227" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-5%20h-5%20rounded-md%20flex%20items-center%20justify-center%22%7D"
              className="w-5 h-5 rounded-md flex items-center justify-center"
              style={{ background: `${laneColor}20`, color: laneColor }}
            >
              {CATEGORY_ICONS[node.category] || <LayoutGrid data-component-id="src/app/components/SpecMapNode.tsx:226:48" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="226" data-component-end-line="226" data-component-file="SpecMapNode.tsx" data-component-name="LayoutGrid" data-component-content="%7B%22elementName%22%3A%22LayoutGrid%22%7D" size={12} />}
            </div>
            <span data-component-id="src/app/components/SpecMapNode.tsx:228:12" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="228" data-component-end-line="230" data-component-file="SpecMapNode.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xs%20font-medium%20text-muted-foreground%20uppercase%20tracking-wider%22%7D" className="text-2xs font-medium text-muted-foreground uppercase tracking-wider">
              {node.category}
            </span>
          </div>
          <span data-component-id="src/app/components/SpecMapNode.tsx:232:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="232" data-component-end-line="234" data-component-file="SpecMapNode.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`text-2xs px-1.5 py-0.5 rounded font-medium ${priority.className}`}>
            {node.priority}
          </span>
        </div>

        {/* Title */}
        <div data-component-id="src/app/components/SpecMapNode.tsx:238:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="238" data-component-end-line="240" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-foreground%20leading-snug%20mb-1.5%22%7D" className="text-sm font-semibold text-foreground leading-snug mb-1.5">
          {node.title}
        </div>

        {/* Short description */}
        <p data-component-id="src/app/components/SpecMapNode.tsx:243:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="243" data-component-end-line="245" data-component-file="SpecMapNode.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-2xs%20text-muted-foreground%20leading-relaxed%20line-clamp-2%22%7D" className="text-2xs text-muted-foreground leading-relaxed line-clamp-2">
          {node.description}
        </p>
      </div>

      {/* Divider */}
      <div data-component-id="src/app/components/SpecMapNode.tsx:249:6" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="249" data-component-end-line="249" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-px%20bg-border%20mx-3.5%22%7D" className="h-px bg-border mx-3.5" />

      {/* Footer */}
      <div data-component-id="src/app/components/SpecMapNode.tsx:252:6" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="252" data-component-end-line="284" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-3.5%20py-2.5%20flex%20items-center%20justify-between%20gap-2%22%7D" className="px-3.5 py-2.5 flex items-center justify-between gap-2">
        {/* Status badge */}
        <span data-component-id="src/app/components/SpecMapNode.tsx:254:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="254" data-component-end-line="257" data-component-file="SpecMapNode.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`flex items-center gap-1 text-2xs px-1.5 py-0.5 rounded font-medium ${status.className}`}>
          {status.icon}
          {status.label}
        </span>

        <div data-component-id="src/app/components/SpecMapNode.tsx:259:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="259" data-component-end-line="283" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1.5%22%7D" className="flex items-center gap-1.5">
          {/* Screen count */}
          {node.screenCount > 0 && (
            <span data-component-id="src/app/components/SpecMapNode.tsx:262:12" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="262" data-component-end-line="265" data-component-file="SpecMapNode.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20text-2xs%20text-muted-foreground%22%7D" className="flex items-center gap-1 text-2xs text-muted-foreground">
              <Monitor data-component-id="src/app/components/SpecMapNode.tsx:263:14" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="263" data-component-end-line="263" data-component-file="SpecMapNode.tsx" data-component-name="Monitor" data-component-content="%7B%22elementName%22%3A%22Monitor%22%7D" size={9} />
              {node.screenCount}
            </span>
          )}

          {/* Dependency count */}
          {node.dependencies.length > 0 && (
            <span data-component-id="src/app/components/SpecMapNode.tsx:270:12" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="270" data-component-end-line="273" data-component-file="SpecMapNode.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20text-2xs%20text-muted-foreground%22%7D" className="flex items-center gap-1 text-2xs text-muted-foreground">
              <GitBranch data-component-id="src/app/components/SpecMapNode.tsx:271:14" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="271" data-component-end-line="271" data-component-file="SpecMapNode.tsx" data-component-name="GitBranch" data-component-content="%7B%22elementName%22%3A%22GitBranch%22%7D" size={9} />
              {node.dependencies.length}
            </span>
          )}

          {/* Expand toggle */}
          <button data-component-id="src/app/components/SpecMapNode.tsx:277:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="277" data-component-end-line="282" data-component-file="SpecMapNode.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-0.5%20rounded%20text-muted-foreground%20hover%3Atext-foreground%20transition-colors%22%7D"
            onClick={handleExpand}
            className="p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? <ChevronUp data-component-id="src/app/components/SpecMapNode.tsx:281:24" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="281" data-component-end-line="281" data-component-file="SpecMapNode.tsx" data-component-name="ChevronUp" data-component-content="%7B%22elementName%22%3A%22ChevronUp%22%7D" size={11} /> : <ChevronDown data-component-id="src/app/components/SpecMapNode.tsx:281:50" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="281" data-component-end-line="281" data-component-file="SpecMapNode.tsx" data-component-name="ChevronDown" data-component-content="%7B%22elementName%22%3A%22ChevronDown%22%7D" size={11} />}
          </button>
        </div>
      </div>

      {/* Expanded child screens */}
      {expanded && node.childScreens.length > 0 && (
        <div data-component-id="src/app/components/SpecMapNode.tsx:288:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="288" data-component-end-line="309" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-t%20border-border%20mx-0%20px-3.5%20pb-3%20pt-2.5%20animate-fade-in%22%7D" className="border-t border-border mx-0 px-3.5 pb-3 pt-2.5 animate-fade-in">
          <div data-component-id="src/app/components/SpecMapNode.tsx:289:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="289" data-component-end-line="291" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-2xs%20font-semibold%20uppercase%20tracking-widest%20text-muted-foreground%20mb-2%22%2C%22textContent%22%3A%22Screens%22%7D" className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
            Screens
          </div>
          <div data-component-id="src/app/components/SpecMapNode.tsx:292:10" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="292" data-component-end-line="308" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-1.5%22%7D" className="flex flex-col gap-1.5">
            {node.childScreens.map(screen => (
              <div data-component-id="src/app/components/SpecMapNode.tsx:294:14" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="294" data-component-end-line="306" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20group%22%7D"
                key={`${node.id}-screen-${screen.id}`}
                className="flex items-center gap-2 group"
              >
                <ArrowRight data-component-id="src/app/components/SpecMapNode.tsx:298:16" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="298" data-component-end-line="298" data-component-file="SpecMapNode.tsx" data-component-name="ArrowRight" data-component-content="%7B%22elementName%22%3A%22ArrowRight%22%2C%22className%22%3A%22text-muted-foreground%20flex-shrink-0%22%7D" size={9} className="text-muted-foreground flex-shrink-0" />
                <span data-component-id="src/app/components/SpecMapNode.tsx:299:16" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="299" data-component-end-line="301" data-component-file="SpecMapNode.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xs%20text-muted-foreground%20group-hover%3Atext-foreground%20transition-colors%22%7D" className="text-2xs text-muted-foreground group-hover:text-foreground transition-colors">
                  {screen.label}
                </span>
                <div data-component-id="src/app/components/SpecMapNode.tsx:302:16" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="302" data-component-end-line="302" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1" />
                <span data-component-id="src/app/components/SpecMapNode.tsx:303:16" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="303" data-component-end-line="305" data-component-file="SpecMapNode.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`text-2xs px-1 py-px rounded ${STATUS_CONFIG[screen.status]?.className || 'badge-draft'}`}>
                  {screen.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Owner chip */}
      <div data-component-id="src/app/components/SpecMapNode.tsx:313:6" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="313" data-component-end-line="328" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20-bottom-2.5%20left-3.5%20flex%20items-center%20gap-1%20px-1.5%20py-0.5%20rounded-full%20border%20text-2xs%22%7D"
        className="absolute -bottom-2.5 left-3.5 flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-2xs"
        style={{
          background: '#1A1714',
          borderColor: '#3A3430',
          color: '#8A7F78',
        }}
      >
        <div data-component-id="src/app/components/SpecMapNode.tsx:321:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="321" data-component-end-line="326" data-component-file="SpecMapNode.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-3%20h-3%20rounded-full%20flex%20items-center%20justify-center%20text-2xs%20font-bold%22%7D"
          className="w-3 h-3 rounded-full flex items-center justify-center text-2xs font-bold"
          style={{ background: laneColor, color: '#fff', fontSize: '7px' }}
        >
          {node.owner.split(' ').map(p => p[0]).join('')}
        </div>
        <span data-component-id="src/app/components/SpecMapNode.tsx:327:8" data-component-path="src/app/components/SpecMapNode.tsx" data-component-line="327" data-component-end-line="327" data-component-file="SpecMapNode.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{node.owner.split(' ')[0]}</span>
      </div>
    </div>
  );
}
